
import { useState, Suspense, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { Button, Card, Input } from '@/components/ui';
import { BrandingLogoHeader } from '@/components/layout/BrandingLogoHeader';

function LoginForm() {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const { login, isLoading: authLoading, isAuthenticated, user } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasRedirected = useRef(false);

  // Redirect if already authenticated: use ?redirect= only when safe (same-origin relative path), else role-based default
  useEffect(() => {
    if (!isAuthenticated || authLoading || hasRedirected.current || pathname !== '/login') return;

    const explicitRedirect = searchParams.get('redirect');
    // Allow only same-origin relative paths: starts with /, not //, no colon (reject protocol-relative or absolute URLs)
    const isSafeRedirect =
      typeof explicitRedirect === 'string' &&
      explicitRedirect.startsWith('/') &&
      !explicitRedirect.startsWith('//') &&
      !explicitRedirect.includes(':');

    const targetPath = isSafeRedirect
      ? explicitRedirect
      : user?.role === 'admin'
        ? '/admin'
        : user?.role === 'producer'
          ? '/producer'
          : user?.role === 'moderator'
            ? '/submissions'
            : user?.role === 'prayer_partner'
              ? '/submissions'
              : '/admin';

    hasRedirected.current = true;
    if (targetPath !== pathname) {
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, pathname, navigate, searchParams, user?.role]);

  // Don't render form if already authenticated
  if (isAuthenticated && !authLoading) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      // Reset flag so useEffect can redirect
      hasRedirected.current = false;
      setIsSubmitting(false);
      // The useEffect will handle redirect when isAuthenticated becomes true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid email or password';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Card variant="elevated" padding="none" className="p-6 sm:p-8 space-y-5">
      <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
        disabled={isSubmitting}
        placeholder="you@example.com"
      />

      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="current-password"
        disabled={isSubmitting}
        placeholder="Enter your password"
      />

      <Button
        type="submit"
        fullWidth
        isLoading={isSubmitting}
        disabled={isSubmitting || !email || !password}
      >
        Sign In
      </Button>
    </form>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <BrandingLogoHeader className="text-3xl" />
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Staff Login
          </p>
        </div>

        {/* Login Form wrapped in Suspense */}
        <Suspense fallback={
          <Card variant="elevated" padding="lg" className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </Card>
        }>
          <LoginForm />
        </Suspense>

        {/* Back to home link */}
        <p className="text-center mt-6 text-sm text-slate-600 dark:text-slate-400">
          <Link to="/" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            ← Back to home
          </Link>
        </p>
      </div>
    </main>
  );
}
